(() => {
  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const normalizeTitle = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const getVariant = (product, packageKey) => {
    if (!product || !Array.isArray(product.variants)) return null;
    const wanted = normalizeTitle(packageKey);
    return (
      product.variants.find((variant) => normalizeTitle(variant.title) === wanted) ||
      product.variants.find((variant) => normalizeTitle(variant.title).includes(wanted)) ||
      null
    );
  };

  const parseProduct = (root, slot) => {
    const node = root.querySelector(`[data-hio-product-json="${slot}"]`);
    if (!node) return null;
    try {
      const parsed = JSON.parse(node.textContent);
      return parsed && parsed.id ? parsed : null;
    } catch (_error) {
      return null;
    }
  };

  const fetchProduct = async (root, slot) => {
    const handle = root.dataset[`${slot}ProductHandle`];
    if (!handle) return null;

    try {
      const routeRoot = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${routeRoot}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;
      const product = await response.json();
      return product && product.id ? product : null;
    } catch (_error) {
      return null;
    }
  };

  const buildOdometer = (root) => {
    const node = root.querySelector("[data-hio-odometer]");
    if (!node || node.dataset.ready === "true") return;
    const number = String(node.dataset.number || "5000").replace(/\D/g, "") || "5000";
    const prefix = document.createElement("span");
    prefix.textContent = "$";
    node.appendChild(prefix);

    const digitsWrap = document.createElement("span");
    digitsWrap.className = "hio-odometer";

    number.split("").forEach((digit, index) => {
      if (index > 0 && (number.length - index) % 3 === 0) {
        const comma = document.createElement("span");
        comma.textContent = ",";
        digitsWrap.appendChild(comma);
      }

      const digitWrap = document.createElement("span");
      digitWrap.className = "hio-odometer-digit";
      const reel = document.createElement("span");
      reel.className = "hio-odometer-reel";
      reel.style.setProperty("--hio-delay", `${index * 90}ms`);

      const target = Number(digit);
      const rollCount = 20 + target;
      reel.style.setProperty("--hio-roll-count", String(rollCount));
      for (let step = 0; step <= rollCount; step += 1) {
        const value = document.createElement("span");
        value.textContent = String(step % 10);
        reel.appendChild(value);
      }

      digitWrap.appendChild(reel);
      digitsWrap.appendChild(digitWrap);
    });

    node.appendChild(digitsWrap);
    const plus = document.createElement("span");
    plus.className = "hio-plus";
    plus.textContent = "+";
    node.appendChild(plus);
    node.dataset.ready = "true";

    const start = () => {
      node.querySelectorAll(".hio-odometer-reel").forEach((reel) => reel.classList.add("is-rolling"));
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            window.setTimeout(start, 200);
            observer.disconnect();
          }
        },
        { threshold: 0.45 },
      );
      observer.observe(node);
    } else {
      start();
    }
  };

  const initialize = async (root) => {
    if (!root || root.dataset.hioReady === "true") return;
    root.dataset.hioReady = "true";
    buildOdometer(root);

    const products = {
      month1: parseProduct(root, "month1"),
      month2: parseProduct(root, "month2"),
    };

    if (!products.month1) products.month1 = await fetchProduct(root, "month1");
    if (!products.month2) products.month2 = await fetchProduct(root, "month2");

    let selectedMonth = "month1";
    let selectedPackage = "Triple";

    const monthButtons = Array.from(root.querySelectorAll("[data-hio-month]"));
    const packageCards = Array.from(root.querySelectorAll("[data-hio-package]"));
    const checkoutButton = root.querySelector("[data-hio-checkout]");
    const checkoutMonth = root.querySelector("[data-hio-checkout-month]");
    const checkoutPackage = root.querySelector("[data-hio-checkout-package]");
    const checkoutPrice = root.querySelector("[data-hio-checkout-price]");
    const checkoutSummary = root.querySelector("[data-hio-checkout-summary]");
    const checkoutMessage = root.querySelector("[data-hio-checkout-message]");
    const checkoutTrackmanId = root.querySelector("[data-hio-trackman-id]");
    const monthWarning = root.querySelector("[data-hio-month-warning]");

    const getSelectedMonthButton = () =>
      monthButtons.find((button) => button.dataset.hioMonth === selectedMonth) || monthButtons[0];

    const update = () => {
      const monthButton = getSelectedMonthButton();
      const monthLabel = monthButton?.dataset.monthLabel || "Selected month";
      const product = products[selectedMonth];

      monthButtons.forEach((button) => {
        const isSelected = button.dataset.hioMonth === selectedMonth;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", String(isSelected));
        const status = button.querySelector("[data-hio-month-status]");
        if (status) status.textContent = isSelected ? "SELECTED CHALLENGE" : "MONTHLY ENTRY";
      });

      if (monthWarning) {
        monthWarning.textContent = `Your ${monthLabel} entry applies only to the ${monthLabel} Skill Test. It does not include entry into any following month.`;
      }

      packageCards.forEach((card) => {
        const packageName = card.dataset.hioPackage || "";
        const variant = getVariant(product, packageName);
        const isSelected = normalizeTitle(packageName) === normalizeTitle(selectedPackage);
        card.classList.toggle("is-selected", isSelected);

        const tag = card.querySelector("[data-hio-entry-tag]");
        if (tag) tag.textContent = `${monthLabel.toUpperCase()} · ${card.dataset.tag || ""}`;

        const price = card.querySelector("[data-hio-price]");
        if (price && variant) price.textContent = formatMoney(variant.price).replace("$", "");

        const selectButton = card.querySelector("[data-hio-select-package]");
        if (selectButton) {
          selectButton.disabled = Boolean(product && (!variant || !variant.available));
          selectButton.textContent = isSelected ? "SELECTED →" : `CHOOSE ${packageName.toUpperCase()} →`;
        }
      });

      const selectedCard = packageCards.find(
        (card) => normalizeTitle(card.dataset.hioPackage) === normalizeTitle(selectedPackage),
      );
      const variant = getVariant(product, selectedPackage);
      const attempts = selectedCard?.dataset.attempts || "";
      const perAttempt = selectedCard?.dataset.perAttempt || "";

      if (checkoutMonth) checkoutMonth.textContent = `${monthLabel.toUpperCase()} SKILL TEST`;
      if (checkoutPackage) checkoutPackage.textContent = selectedPackage;
      if (checkoutPrice) {
        checkoutPrice.textContent = variant
          ? formatMoney(variant.price)
          : selectedCard?.dataset.fallbackPrice || "";
      }
      if (checkoutSummary) {
        checkoutSummary.textContent = `${attempts} verified attempts · ${perAttempt} per attempt · valid only for ${monthLabel}`;
      }

      if (!checkoutButton || !checkoutMessage) return;
      if (!product) {
        checkoutButton.disabled = true;
        checkoutMessage.textContent = "Select this month’s Shopify product in the theme editor to activate checkout.";
      } else if (!variant) {
        checkoutButton.disabled = true;
        checkoutMessage.textContent = `The ${product.title} product needs a variant named “${selectedPackage}”.`;
      } else if (!variant.available) {
        checkoutButton.disabled = true;
        checkoutMessage.textContent = "This entry option is currently unavailable.";
      } else {
        checkoutButton.disabled = false;
        checkoutMessage.textContent = "";
        checkoutButton.dataset.variantId = String(variant.id);
        checkoutButton.dataset.monthLabel = monthLabel;
      }
    };

    monthButtons.forEach((button) => {
      button.addEventListener("click", () => {
        selectedMonth = button.dataset.hioMonth || "month1";
        update();
      });
    });

    packageCards.forEach((card) => {
      const selectButton = card.querySelector("[data-hio-select-package]");
      if (!selectButton) return;
      selectButton.addEventListener("click", () => {
        selectedPackage = card.dataset.hioPackage || "Triple";
        update();
        root.querySelector("#hio-checkout")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });

    root.querySelectorAll("[data-hio-scroll]").forEach((link) => {
      link.addEventListener("click", (event) => {
        const selector = link.getAttribute("href");
        if (!selector || !selector.startsWith("#")) return;
        const target = root.querySelector(selector);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    root.querySelectorAll("[data-hio-faq-button]").forEach((button) => {
      button.addEventListener("click", () => {
        const item = button.closest(".hio-faq-item");
        if (!item) return;
        const wasOpen = item.classList.contains("is-open");
        root.querySelectorAll(".hio-faq-item").forEach((otherItem) => {
          otherItem.classList.remove("is-open");
          otherItem.querySelector("[data-hio-faq-button]")?.setAttribute("aria-expanded", "false");
          const symbol = otherItem.querySelector(".hio-faq-symbol");
          if (symbol) symbol.textContent = "+";
        });
        if (!wasOpen) {
          item.classList.add("is-open");
          button.setAttribute("aria-expanded", "true");
          const symbol = item.querySelector(".hio-faq-symbol");
          if (symbol) symbol.textContent = "−";
        }
      });
    });

    checkoutButton?.addEventListener("click", async () => {
      const variantId = Number(checkoutButton.dataset.variantId);
      const monthLabel = checkoutButton.dataset.monthLabel || "";
      if (!variantId || !checkoutMessage) return;
      const trackmanId = checkoutTrackmanId?.value.trim() || "";
      const cartUrl = window.Shopify?.routes?.cart_url || "/cart";
      const submitToCheckout = (destination) => {
        if (destination === "cart") {
          window.location.assign(cartUrl);
          return;
        }

        const checkoutForm = document.createElement("form");
        checkoutForm.method = "post";
        checkoutForm.action = cartUrl;
        checkoutForm.target = window.self !== window.top ? "_top" : "_self";
        checkoutForm.style.display = "none";

        const checkoutSubmit = document.createElement("button");
        checkoutSubmit.type = "submit";
        checkoutSubmit.name = "checkout";
        checkoutSubmit.value = "Checkout";
        checkoutSubmit.hidden = true;
        checkoutForm.append(checkoutSubmit);
        document.body.append(checkoutForm);
        if (typeof checkoutForm.requestSubmit === "function") {
          checkoutForm.requestSubmit(checkoutSubmit);
        } else {
          checkoutSubmit.click();
        }
      };

      checkoutButton.disabled = true;
      checkoutButton.setAttribute("aria-busy", "true");
      checkoutMessage.textContent = "Preparing your secure checkout…";

      try {
        const checkoutDestination = root.dataset.checkoutDestination === "cart" ? "cart" : "checkout";
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
          headers: { Accept: "application/json" },
        });
        const cart = await cartResponse.json();
        const alreadyHasEntry = Array.isArray(cart.items) && cart.items.some((item) => {
          const properties = item.properties || {};
          return (
            String(properties["_HIO Skill Test"] || "") === "true" &&
            String(properties["Challenge Month"] || "") === monthLabel
          );
        });

        if (alreadyHasEntry) {
          checkoutMessage.textContent = `Your ${monthLabel} entry is already in the cart. Opening secure checkout…`;
          const fallbackLink = document.createElement("a");
          fallbackLink.href = cartUrl;
          fallbackLink.textContent = "Review cart →";
          checkoutMessage.append(" ", fallbackLink);
          submitToCheckout(checkoutDestination);
          return;
        }

        const lineItemProperties = {
          "Challenge Month": monthLabel,
          "_HIO Skill Test": "true",
        };
        if (trackmanId) lineItemProperties["TrackMan ID"] = trackmanId;

        const addResponse = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: lineItemProperties,
              },
            ],
          }),
        });

        if (!addResponse.ok) {
          const error = await addResponse.json().catch(() => ({}));
          throw new Error(error.description || "The entry could not be added to your cart.");
        }

        try {
          window.sessionStorage.setItem("nextGolfHioCart", "true");
        } catch (_error) {
          // Checkout still works when browser storage is unavailable.
        }

        checkoutMessage.textContent = "Entry added. Opening secure checkout…";
        const fallbackLink = document.createElement("a");
        fallbackLink.href = cartUrl;
        fallbackLink.textContent = "Review cart →";
        checkoutMessage.append(" ", fallbackLink);
        submitToCheckout(checkoutDestination);
      } catch (error) {
        checkoutMessage.textContent = error instanceof Error ? error.message : "Something went wrong. Please try again.";
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute("aria-busy");
      }
    });

    update();
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll("[data-hio-root]").forEach(initialize);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
